import React, { useState } from "react";
import api, { API, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileDown, Fingerprint, Sparkles, Flag, ThumbsUp, Loader2, Leaf, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { MaterialGate } from "./MaterialGate";
import { ResubmitChecklist, VersionBar } from "./Resubmission";

const DIMS = [["d3", "3D Massing"], ["d4", "4D Schedule"], ["d5", "5D Cost & Valuation"], ["d6", "6D Carbon"], ["d7", "7D Lifecycle O&M"], ["d8", "8D Safety · AFAD"], ["d9", "9D Social Value"]];
const GRADE = { A: "#00FF7F", B: "#84cc16", C: "#F59E0B", D: "#F97316", E: "#EF4444" };
const color = (s) => (s >= 75 ? "#00FF7F" : s >= 55 ? "#F59E0B" : "#EF4444");
const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : String(n));

export const TenderDetail = ({ t, onChanged, onOpen, onRescored }) => {
  const { user } = useAuth();
  const [kind, setKind] = useState("attestation");
  const [statement, setStatement] = useState("");
  const [busy, setBusy] = useState(false);
  const d = t.dimensions;

  const claim = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/justice/claims", { city: t.city, kind, statement, target_type: "tender", target_id: t.id, verdict_hash: t.anchor });
      toast.success(`${kind} anchored on the ledger`); setStatement(""); onChanged();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="qs-grad-border p-6 space-y-5" data-testid="tender-detail">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">{t.project_type.replace("_", " ")} · {t.city} · {t.authority}</p>
          <h3 className="font-display font-bold text-2xl mt-1">{t.title}{(t.version || 1) > 1 && <span className="ml-2 text-xs font-mono text-[#F59E0B] align-middle" data-testid="tender-version-badge">v{t.version}</span>}</h3>
          <p className="text-xs font-mono text-slate-500 mt-1">{fmt(t.floor_area_m2)} m² · {t.stories} storeys · {fmt(t.budget_try)} TRY · {t.timeline_months} months · soil {t.soil_class}{t.base_isolation ? " · base-isolated" : ""}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-5xl font-bold" style={{ color: GRADE[t.grade] }} data-testid="tender-score">{t.feasibility_score}</div>
          <div className="text-xs font-mono text-slate-400">grade {t.grade} · <span data-testid="tender-recommendation">{t.recommendation}</span></div>
          {t.scored_feasibility != null && <div className="text-[10px] font-mono text-[#EF4444]" data-testid="tender-scored-feasibility">gate failed · would have scored {t.scored_feasibility}</div>}
        </div>
      </div>

      <VersionBar t={t} onOpen={onOpen || (() => {})} />
      <MaterialGate g={t.material_gate} />
      <ResubmitChecklist key={t.id} t={t} onChanged={onChanged} onRescored={onRescored} />

      <div className="space-y-2" data-testid="tender-dimensions">
        {DIMS.map(([k, name]) => (
          <div key={k} data-testid={`dim-${k}`}>
            <div className="flex justify-between text-xs"><span className="text-slate-300">{name} <span className="text-slate-600 font-mono">×{t.weights[k]}</span></span><span className="font-mono" style={{ color: color(d[k].score) }}>{d[k].score}</span></div>
            <div className="h-1.5 bg-[#050A15] mt-1"><div className="h-full transition-all duration-700" style={{ width: `${d[k].score}%`, background: color(d[k].score) }} /></div>
          </div>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-3 text-xs">
        <div className="bg-[#050A15] border border-white/5 p-3 space-y-1 font-mono" data-testid="tender-cost">
          <div className="text-slate-500 uppercase text-[10px]">5D cost & valuation</div>
          <div>Estimate <span className="text-slate-200">{fmt(d.d5.estimated_cost_try)} TRY</span> · ratio {d.d5.budget_ratio}</div>
          <div>Valuation <span className="text-[#00FFFF]">{fmt(d.d5.asset_valuation_try)} TRY</span></div>
          <div>50y O&M <span className="text-slate-200">{fmt(d.d7.lifecycle_50y_try)} TRY</span></div>
        </div>
        <div className="bg-[#050A15] border border-white/5 p-3 space-y-1 font-mono" data-testid="tender-green">
          <div className="text-slate-500 uppercase text-[10px] flex items-center gap-1"><Leaf className="h-3 w-3" /> 6D carbon & green finance</div>
          <div>Embodied <span className="text-slate-200">{fmt(d.d6.embodied_tco2e)} tCO2e</span> ({d.d6.intensity_tco2e_m2} t/m²)</div>
          <div>Offset <span className="text-[#00FF7F]">{d.d6.zcc_offset_needed} ZCC</span> · green bond {d.d6.green_bond_eligible ? <span className="text-[#00FF7F]">eligible</span> : <span className="text-[#F59E0B]">not yet</span>}</div>
          <div>Jobs ≈ {d.d9.jobs_created} · justice index {d.d9.city_justice_index ?? "—"} · coops {d.d9.imece_coops_in_city}</div>
        </div>
      </div>

      {d.d8.afad_flags.length > 0 && (
        <div className="space-y-1" data-testid="afad-flags">
          <div className="text-[10px] uppercase tracking-widest text-[#F59E0B] flex items-center gap-1"><ShieldAlert className="h-3 w-3" /> Seismic / AFAD flags · PGA {d.d8.pga_g}g · D/C {d.d8.demand_capacity}</div>
          {d.d8.afad_flags.map((f) => <p key={f} className="text-xs text-slate-300 border-l-2 border-[#F59E0B]/60 pl-2">{f}</p>)}
        </div>
      )}

      <div data-testid="tender-narrative">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#F59E0B] mb-2"><Sparkles className="h-3.5 w-3.5" /> Analyst narrative · Claude Sonnet 5</div>
        <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{(t.narrative || "").replace(/\*\*/g, "")}</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[10px] font-mono text-slate-500 flex items-center gap-1 break-all" data-testid="tender-anchor"><Fingerprint className="h-3 w-3 text-[#00FFFF]" /> {t.anchor}</span>
        <a data-testid="tender-pdf" href={`${API}/bim/tenders/${t.id}/pdf`} className="ml-auto flex items-center gap-2 border border-white/15 px-3 py-1.5 text-xs hover:border-[#F59E0B] hover:text-[#F59E0B]"><FileDown className="h-3.5 w-3.5" /> PDF</a>
      </div>

      <div data-testid="tender-claims">
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Public scrutiny · {t.claims?.length ?? 0} claim(s)</div>
        {user ? (
          <form onSubmit={claim} className="space-y-2 mb-3" data-testid="tender-claim-form">
            <div className="flex gap-1.5">
              {["attestation", "dispute"].map((k) => (
                <button type="button" key={k} data-testid={`tender-claim-${k}`} onClick={() => setKind(k)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border ${kind === k ? (k === "dispute" ? "border-[#EF4444] text-[#EF4444]" : "border-[#00FF7F] text-[#00FF7F]") : "border-white/10 text-slate-400"}`}>
                  {k === "dispute" ? <Flag className="h-3.5 w-3.5" /> : <ThumbsUp className="h-3.5 w-3.5" />} {k}
                </button>
              ))}
            </div>
            <textarea data-testid="tender-claim-statement" value={statement} onChange={(e) => setStatement(e.target.value)} rows={2} minLength={12} required
              className="w-full bg-[#050A15] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#8A2BE2]" placeholder="What does this assessment get right or wrong?" />
            <button data-testid="tender-claim-submit" disabled={busy || statement.trim().length < 12} className="qs-cta px-4 py-2 text-sm bg-[#8A2BE2] text-white disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anchor on ledger"}</button>
          </form>
        ) : <p className="text-xs text-slate-500 mb-3">Sign in to dispute or attest.</p>}
        <div className="space-y-2 max-h-56 overflow-auto">
          {(t.claims || []).map((c) => (
            <div key={c.id} className={`border-l-2 pl-3 ${c.kind === "dispute" ? "border-[#EF4444]" : "border-[#00FF7F]"}`}>
              <div className="text-[11px] font-mono text-slate-500">{c.kind} · {c.author} · {new Date(c.created_at).toLocaleDateString()}</div>
              <p className="text-sm text-slate-300">{c.statement}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
